import classNames from 'classnames'
import {
  Fragment,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { ResizeContext } from 'core-components/context/resize'
import { Footer } from 'core-components/library/list/table/Footer'
import { LoaderOverlay } from 'core-components/library/loader'
import { BaseCrud } from 'core-components/types'
import { KeyboardContext } from 'core-components/wrappers/keyboard'

import { DEFAULT_EMPTY_PAGE_SIZE } from './constants/tableDefaultConfig'
import { getKeyField, getSource } from './helpers'
import { getColumnsFromStorage } from './helpers/columnStorageHelper'
import { getSavedFilter, setSavedFilter } from './helpers/filterStorageHelper'
import { prepareColumns } from './helpers/prepareColumns'
import { addDrag } from './table/helpers/addDrag'
import { getHierarchy } from './table/helpers/getHierarchy'
import { getValue } from './table/helpers/utils'
import { useKeysSettings } from './table/hooks/useKeysSettings'
import { useRecalculatingScroll } from './table/hooks/useRecalculatingScroll'
import { useSource } from './table/hooks/useSource'
import { useTemplates } from './table/hooks/useTemplates'
import { ITHeadProps } from './table/models/ITHead'
import { TableHeader } from './table/TableHeader'
import { PlaceholderRows } from './table/tablePlaceholder/PlaceholderRows'
import { THead } from './table/THead'
import { IColumn, ICustomRowOptions, IRowOptions, RowState, TableMode, TTableProps } from './types'

import styles from './Table.module.scss'

export function Table<P = IForm, R = string | number>(props: TTableProps<P, R>) {
  const [resizeCountContext, setResizeCountContext] = useContext(ResizeContext)

  const containerRef = useRef<HTMLTableElement>(null)
  const scrollRefLocal = useRef<HTMLDivElement>(null)
  const scrollRef = props.scrollRefObject || scrollRefLocal

  const [immutable] = useState<any>({})
  const [selected, setSelected] = useState<string>('')
  const [rows, setRows] = useState<{ [propName: string]: RowState }>({})
  const [columns, setColumns] = useState(() =>
    prepareColumns(props.columns, props.rowActions, props.rowActionsVisible)
  )

  const source: BaseCrud<P, R> = useMemo(
    () =>
      getSource({
        source: props.source,
        items: props.items,
        keyField: props.keyField,
        pageSize: props.pageSize,
      }),
    [props.items, props.keyField, props.pageSize, props.source]
  )

  const keyField = getKeyField({ source, keyField: props.keyField })

  const crudFilter = useMemo(
    () => getSavedFilter(source, props.localFiltersKey),
    [props.localFiltersKey, source]
  )

  const { registerKeyboardEvent, prioritizeKeyboard } = useKeysSettings({
    useHotKeys: !!props.useHotKeys,
  })
  const { rowTpl, columnTpl } = useTemplates<P, R>({ ...props, columns })
  const { wip, data, openNode, total } = useSource<P, R>({
    source,
    converter: props.convertIFormToP,
    sourceUpdateCount: props.sourceUpdateCount,
    filters: props.filter || crudFilter,
    measure: props.measure,
    onDataLoad: props.onDataLoad,
    onDataLoadError: props.onDataLoadError,
  })
  const isLoaderOverlayVisible = wip && !!data.length
  const noData = (!total || total === -1) && !data.length
  const isPlaceholderVisible = wip || noData
  const emptyPageSize = props.emptyPageSize || DEFAULT_EMPTY_PAGE_SIZE

  const { hasHorizontalScroll, hasVerticalScroll, scrollButtons, calcScrollButtonVisible } =
    useRecalculatingScroll({
      containerRef,
      scrollRef,
      recalculateData: [data, rows, wip, total],
    })

  const enableNav = scrollButtons.next !== 'hidden' || scrollButtons.previous !== 'hidden'

  const onScrollButtonClickHandler = useCallback(
    (direction: 'next' | 'previous') => {
      const container = containerRef.current
      if (container) {
        const delta = direction === 'next' ? 1 : -1
        const px = container.clientWidth / 3
        if (scrollRef.current) {
          const newScrollPosition = scrollRef.current.scrollLeft + px * delta
          scrollRef.current.scrollTo({
            left: newScrollPosition,
            behavior: 'smooth',
          })
          calcScrollButtonVisible(newScrollPosition)
        }
      }
    },
    [calcScrollButtonVisible, scrollRef]
  )

  const updateColumns = useCallback((newColumns: IColumn[]) => {
    const notSerializableData: Partial<keyof IColumn>[] = ['titleRender', 'renderFunction']
    const hasNotSerializableData = newColumns.some((column) => {
      return Object.keys(column).some((field) =>
        notSerializableData.includes(field as keyof IColumn)
      )
    })

    setColumns((prev) => {
      if (JSON.stringify(newColumns) !== JSON.stringify(prev) || hasNotSerializableData) {
        return newColumns
      }
      return prev
    })
  }, [])

  const rowClick = (item: P, e: MouseEvent) => {
    const key = getValue(item, keyField)
    setSelected(key)
    if (props.onSelect) {
      props.onSelect([key])
    }

    const hierarchy = getHierarchy(props, rows, setRows, openNode, source)
    const hierarchyData = {
      hierarchyClick: hierarchy.hierarchyClick,
      isExpanded: hierarchy.isExpanded,
      event: e,
    }

    if (props.onClick) {
      props.onClick(item, hierarchyData)
    }

    // setFocusOn(key)
  }

  const highlightRows = (rows: { [propName: string]: RowState }) => {
    if (props.selectedRows) {
      Object.keys(rows).forEach((key) => {
        rows[key].selected = false
      })
      props.selectedRows.forEach((key) => {
        const current = rows[key] || {}
        current.selected = true
        rows[key] = current
      })
    }
  }

  const resizeContextCall = (delayed: boolean) => {
    if (setResizeCountContext && resizeCountContext) {
      if (delayed) {
        setTimeout(() => {
          setResizeCountContext(resizeCountContext + 1)
        }, 0)
      } else {
        setResizeCountContext(resizeCountContext + 1)
      }
    }
  }

  useEffect(() => {
    const columnsData = prepareColumns(props.columns, props.rowActions, props.rowActionsVisible)
    updateColumns(columnsData)
  }, [props.columns, props.rowActions, props.rowActionsVisible, updateColumns])

  useEffect(() => {
    return source.subscribe('change', () => {
      setSavedFilter(source.getFilter(), props.localFiltersKey)
    })
  }, [props.localFiltersKey, source])

  useEffect(() => {
    const cacheColumns = getColumnsFromStorage(props.columnsFilterKey)
    updateColumns(cacheColumns || columns)
  }, [props.columnsFilterKey, updateColumns])

  useEffect(() => {
    if (props.expanded && props.expanded.length) {
      let expandedRows = { ...rows }
      props.expanded.forEach(async (key: number) => {
        const current = rows[key] || {}
        if (!current.expanded) {
          current.expanded = true
          expandedRows = {
            ...expandedRows,
            [key]: {
              ...current,
            },
          }
          await openNode(key)
        }
      })
      highlightRows(expandedRows)
      setRows(expandedRows)
    } else {
      const collapsedRows = {}
      const keys = Object.keys(rows)
      for (const rowId of keys) {
        collapsedRows[rowId] = { ...rows[rowId], expanded: false }
      }
      if (JSON.stringify(collapsedRows) !== JSON.stringify(rows)) {
        highlightRows(collapsedRows)
        setRows(collapsedRows)
      }
    }
  }, [props.expanded, props.selectedRows])

  useEffect(() => {
    if (props.useRowCheckbox && props.checkedValues) {
      setRows((rows) => {
        const newRows = { ...rows }
        for (const key of Object.keys(newRows)) {
          newRows[key].indeterminate = false
          newRows[key].selected = false
        }
        props.checkedValues?.forEach((item) => {
          newRows[item.key] = {
            ...newRows[item.key],
            indeterminate: item.value === 'indeterminate',
            selected: item.value === 'full',
          }
        })
        return newRows
      })
    }
  }, [props.checkedValues, props.useRowCheckbox])

  useEffect(() => {
    resizeContextCall(true)
  }, [data, rows])

  useEffect(() => {
    setTimeout(() => {
      resizeContextCall(false)
    }, 0)
  }, [hasHorizontalScroll])

  const dataMap = (root: any, deep: number = 0, parentKeys: Set<unknown> = new Set()) => {
    const dataRender = props.hasHierarchy
      ? data.filter((el) => getValue(el, source.hierarchyField) == root)
      : data

    if (!immutable.enterClick) {
      immutable['enterClick'] = {}
    }
    if (!immutable.parentNext) {
      immutable['parentNext'] = {}
    }
    if (!immutable.parentPrev) {
      immutable['parentPrev'] = {}
    }

    const itemDrawer = (opt: { index: number; rowOptions?: ICustomRowOptions }) => {
      const el = dataRender[opt.index]

      const pk = getValue(el, keyField)
      const parent = source.hierarchyField ? getValue(el, source.hierarchyField) : ''
      if (props.hasHierarchy && parentKeys.has(pk)) {
        return <Fragment key={`row-fr-${opt.index}`} />
      }
      const childParentKeys = props.hasHierarchy ? new Set(parentKeys).add(pk) : parentKeys

      const rowState = rows[pk] || {}
      immutable.enterClick[pk] = { el, pk }

      const config: IRowOptions = {
        ...(opt.rowOptions || {}),
        deep,
        key: `row-${opt.index}`,
        testId: `table-row-${opt.index}`,
        index: opt.index,
        onClick: (e: MouseEvent) => {
          e.stopPropagation()
          rowClick(el, e)
        },
        className: classNames(
          styles.viewRow,
          styles[`rowActions-${props.rowActionsVisible || 'hover'}`],
          props.mode !== TableMode.div ? styles.row : '',
          selected === pk || rowState.selected
            ? props.selectedRowClassName || styles.viewRow_selected
            : '',
          // focusOn === pk ? props.focusedRowClassName || styles.viewRow_focused : '',
          opt.rowOptions?.className
        ),
      }

      // calcNextAndPrevByKeyboard(root, focusOn, dataRender, rows, immutable, keyField, pk, opt.index)

      const baseConfig = props.useDrag
        ? addDrag(config, pk, opt.index, parent, rows, immutable, props.onMove)
        : config
      return (
        <Fragment key={`row-fr-${opt.index}`}>
          {rowTpl(
            el,
            columnTpl,
            rowState,
            baseConfig,
            props.hasHierarchy ? { ...getHierarchy(props, rows, setRows, openNode, source) } : {},
            '',
            (item, value) => {
              if (props.onCheck) {
                props.onCheck(item, value, data)
              }
            }
          )}
          {rowState.expanded && props.hasHierarchy && dataMap(pk, deep + 1, childParentKeys)}
        </Fragment>
      )
    }

    if (props.itemIterator) {
      return props.itemIterator({ iterator: itemDrawer })
    }

    return (
      <>
        {dataRender.map((el, index) => {
          return itemDrawer({ index })
        })}
      </>
    )
    // return
  }

  const headerProps = useMemo<ITHeadProps<P, R>>(() => {
    return {
      ...props,
      keyField,
      source,
      columns,
      enableNav,
      OnPriorityChange: (columns: IColumn[]) => {
        updateColumns([...columns])
      },
    }
  }, [props, keyField, source, columns, enableNav, updateColumns])

  const tableHeader = useMemo(() => {
    if (!props.header) {
      return null
    }

    if (props.headerTpl) {
      return props.headerTpl(headerProps)
    }

    return <THead {...headerProps} />
  }, [headerProps, props])

  const tableFooter = useMemo(() => {
    if (!props.footerTpl) {
      return null
    }
    return props.footerTpl({ ...props, source })
  }, [props, source])

  return (
    <KeyboardContext.Consumer>
      {(keyboardContext) => {
        registerKeyboardEvent(keyboardContext)

        return (
          <div className={props.classes?.root || ''}>
            {props.header && props.mode !== TableMode.div ? (
              <TableHeader
                scrollButtons={scrollButtons}
                onScrollButtonClickCallback={onScrollButtonClickHandler}
              />
            ) : null}

            <div
              ref={scrollRef}
              onScroll={() => {
                calcScrollButtonVisible(scrollRef.current?.scrollLeft || 0)
              }}
              className={classNames({
                [styles.scroll]: true,
                [`${props.classes?.scrollRoot}`]: !!props.classes?.scrollRoot,
                'loaded-table': !wip && total,
                'content-overflow-x': hasHorizontalScroll,
                [styles.overlay]: isLoaderOverlayVisible,
              })}
            >
              <LoaderOverlay
                className={props.classes?.loaderOverlay}
                show={isLoaderOverlayVisible}
                backgroundSize={{
                  width: hasHorizontalScroll ? scrollRef.current?.scrollWidth : undefined,
                  height: hasVerticalScroll ? scrollRef.current?.scrollHeight : undefined,
                }}
              />
              {props.mode === TableMode.div && !isPlaceholderVisible ? (
                <div
                  className={classNames(styles.divView, props.className)}
                  tabIndex={props.tabIndex || 0}
                  onFocus={prioritizeKeyboard}
                  data-testid={`table-TableMode-${TableMode.div}`}
                >
                  {dataMap(source.hierarchyRoot)}
                </div>
              ) : (
                <table
                  ref={containerRef}
                  data-testid={`table-TableMode-${TableMode.table}`}
                  className={classNames(props.className || '', {
                    ['content-overflow-x']: isPlaceholderVisible && enableNav,
                    [styles.table]: !isPlaceholderVisible,
                    [styles.table__placeholder]: isPlaceholderVisible,
                  })}
                >
                  {tableHeader}
                  <tbody
                    style={props.styles?.body}
                    className={classNames(styles.tbody, props.classes?.body)}
                    tabIndex={props.tabIndex || 0}
                    onFocus={prioritizeKeyboard}
                  >
                    {isPlaceholderVisible ? (
                      <PlaceholderRows
                        columnsCount={columns.length}
                        rowsCount={
                          source.getPaging().pageSize <= emptyPageSize
                            ? source.getPaging().pageSize
                            : emptyPageSize
                        }
                        noData={noData && !wip}
                        hasHierarchy={props.hasHierarchy}
                        useRowCheckbox={props.useRowCheckbox}
                        isSelect={props.isSelect}
                        placeholderContent={props.placeholderContent}
                      />
                    ) : (
                      dataMap(source.hierarchyRoot)
                    )}
                  </tbody>
                  {tableFooter}
                </table>
              )}
            </div>

            {props.pagination || props.hasExport || props.hasRefresh ? (
              <Footer
                {...props}
                total={total}
                wip={wip}
                columns={columns}
                source={source}
                updateColumns={updateColumns}
              />
            ) : null}
          </div>
        )
      }}
    </KeyboardContext.Consumer>
  )
}
